/**
 * Global error handler for capturing JavaScript errors and sending to Dynatrace
 */

import { getDynatraceLogger, getTraceId } from './dynatrace-logger';

export function setupGlobalErrorHandling(): void {
  // Capture unhandled errors
  window.addEventListener('error', (event: ErrorEvent) => {
    const logger = getDynatraceLogger();
    if (!logger) return;

    logger.logError(
      `Unhandled Error: ${event.message}`,
      event.error || new Error(event.message),
      {
        'error.filename': event.filename,
        'error.lineno': event.lineno,
        'error.colno': event.colno,
        'error.type': 'unhandled',
      }
    );
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const logger = getDynatraceLogger();
    if (!logger) return;

    logger.logError(
      'Unhandled Promise Rejection',
      event.reason,
      {
        'error.type': 'unhandled-promise',
      }
    );
  });

  // Flush logs before page unload
  window.addEventListener('beforeunload', () => {
    const logger = getDynatraceLogger();
    if (logger) {
      logger.flush();
    }
  });
}

/**
 * Log page navigation events
 */
export function logPageView(pageName: string, additionalData?: Record<string, any>): void {
  const logger = getDynatraceLogger();
  if (!logger) return;

  logger.logInfo(`Page View: ${pageName}`, {
    'page.name': pageName,
    'page.url': window.location.href,
    'page.referrer': document.referrer,
    ...additionalData,
  });
}

/**
 * Log user interactions
 */
export function logUserAction(action: string, details?: Record<string, any>): void {
  const logger = getDynatraceLogger();
  if (!logger) return;

  logger.logInfo(`User Action: ${action}`, {
    'user.action': action,
    ...details,
  });
}

/**
 * Log API calls
 */
export function logApiCall(
  method: string,
  url: string,
  statusCode?: number,
  duration?: number,
  error?: unknown
): void {
  const logger = getDynatraceLogger();
  if (!logger) return;

  const attributes = {
    'api.method': method,
    'api.url': url,
    'api.status': statusCode,
    'api.duration': duration,
    ...(getTraceId() ? { 'trace.id': getTraceId() } : {}),
  };

  if (error) {
    logger.logError(`API Error: ${method} ${url}`, error, attributes);
  } else {
    logger.logInfo(`API Call: ${method} ${url}`, attributes);
  }
}
